// File: frontend/src/components/marketing/WhatsAppCta.js
//
// The floating "Chat with PesaFii" link on public marketing and legal pages.
//
// It is a real <a> to wa.me and nothing more. It is not a chat widget: no
// greeting bubble, no pulse, and nothing opens until the visitor clicks. It
// appears once with a short CSS fade, which is skipped under reduced motion.
// Framer Motion is not used for that, so it needs no JavaScript to show up on a
// prerendered page.
//
// Placement: bottom-right, 16px from the edges on phones and 24px from md up,
// clear of the iOS home indicator. Its z-index sits above page content and
// below the header drawer, dialogs and toasts.
//
// Public pages only. The signed-in app and /login never mount it; that corner
// of the authenticated app is reserved for the future in-app assistant.

import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { WHATSAPP_MESSAGES, whatsappUrl } from "../../config/publicSupport";
import { WHATSAPP } from "../../theme";

const appear = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
`;

const LABEL = "Chat with PesaFii";

export default function WhatsAppCta({ message = WHATSAPP_MESSAGES.general }) {
  return (
    <Box
      component="a"
      href={whatsappUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with PesaFii on WhatsApp"
      data-testid="whatsapp-cta"
      sx={(t) => {
        const wa = t.palette.whatsapp || WHATSAPP;
        return {
          position: "fixed",
          right: { xs: 16, md: 24 },
          bottom: {
            xs: "calc(16px + env(safe-area-inset-bottom, 0px))",
            md: "calc(24px + env(safe-area-inset-bottom, 0px))",
          },
          zIndex: t.zIndex.speedDial,
          display: "inline-flex",
          alignItems: "center",
          height: 56,
          minWidth: 56,
          // 14 + 28px glyph + 14 = the 56px circle.
          px: "14px",
          gap: 0,
          borderRadius: 99,
          bgcolor: wa.main,
          color: wa.contrastText,
          textDecoration: "none",
          boxShadow: t.palette.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.55)"
            : "0 8px 24px rgba(15,23,42,0.22), 0 0 0 1px rgba(7,46,22,0.18)",
          "@media (prefers-reduced-motion: no-preference)": {
            animation: `${appear} 320ms ease-out 400ms both`,
            transition: "background-color 0.2s, gap 0.2s",
          },
          "&:hover": { bgcolor: wa.hover },
          "&:focus-visible": {
            outline: `3px solid ${t.palette.text.primary}`,
            outlineOffset: 3,
          },
          // The label is revealed on hover or focus, desktop pointers only. On a
          // phone the button stays a 56px circle.
          "@media (hover: hover) and (min-width: 900px)": {
            "&:hover, &:focus-visible": {
              gap: 1,
              "& .wa-label": { maxWidth: 200 },
            },
          },
          "@media print": { display: "none" },
        };
      }}
    >
      <WhatsAppIcon aria-hidden="true" sx={{ fontSize: 28 }} />
      <Box
        component="span"
        className="wa-label"
        aria-hidden="true"
        sx={{
          display: "inline-block",
          maxWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontWeight: 700,
          fontSize: 15,
          "@media (prefers-reduced-motion: no-preference)": { transition: "max-width 0.25s ease" },
        }}
      >
        {LABEL}
      </Box>
    </Box>
  );
}
